import { MultiComboBox } from "@itandi/itandi-bb-ui/MultiComboBox";
import type { ComponentProps } from "react";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { CurrentFilter } from "~/components/deprecated/CurrentFilter";
import { useStatuses } from "~/hooks/vendors/useStatuses";
import type { DealSearchCondition } from "~/models/Deal";
import type { Status } from "~/models/Status";
import {
	convertToComboBoxOption,
	defaultSelectedOption,
	getWithVendorStatuses
} from "~/models/Status";

type MultiComboBoxOption = Exclude<
	ComponentProps<typeof MultiComboBox>["options"][number],
	{ group: string }
>;

export function StatusCombobox() {
	const { setValue, watch } = useFormContext<DealSearchCondition>();
	const watchedStatusIds = watch("statusId");
	const { data: statuses } = useStatuses().index();

	const vendorStatuses = useMemo(() => {
		if (statuses === null) return null;

		return getWithVendorStatuses(statuses);
	}, [statuses]);

	const selectedStatusOptions = useMemo((): MultiComboBoxOption[] => {
		if (vendorStatuses === null) return [];

		// statusId が未設定(undefined)の場合のみデフォルト選択を適用する
		// 空配列はユーザーが意図的に全て外した状態なのでそのまま返す
		if (watchedStatusIds === undefined) {
			return defaultSelectedOption(vendorStatuses);
		}

		return vendorStatuses
			.filter(({ id }) => watchedStatusIds.includes(id))
			.map((s: Status) => convertToComboBoxOption(s));
	}, [watchedStatusIds, vendorStatuses]);

	const onStatusChange = useCallback(
		(options: MultiComboBoxOption[]): void => {
			setValue(
				"statusId",
				options.map((option) => Number(option.value))
			);
		},
		[setValue]
	);
	const removeStatusOption = (index: number): void => {
		const removedOptions = selectedStatusOptions.filter((_, i) => i !== index);
		setValue(
			"statusId",
			removedOptions.map((option) => Number(option.value))
		);
	};
	const updatedOptions = selectedStatusOptions.map((option, index) => ({
		...option,
		onRemove: (): void => {
			removeStatusOption(index);
		}
	}));

	if (statuses === null) {
		return null;
	}

	return (
		<>
			<MultiComboBox
				onChange={onStatusChange}
				options={
					vendorStatuses?.map((s: Status) => convertToComboBoxOption(s)) ?? []
				}
				placeholder="ステータス"
				showOptions={false}
				value={selectedStatusOptions}
			/>
			<CurrentFilter options={updatedOptions} scroll={false} />
		</>
	);
}
